import type { ComputedSection, TemplateDoc } from '@quickreport/domain';
import { type ReportData, toPrintSection } from '@quickreport/pdf';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { renderPdf } from '../../pdf/client';

// pdf.js is large; load it only when someone opens a preview.
const PdfPages = lazy(() => import('../../pdf/PdfPages').then((m) => ({ default: m.PdfPages })));

/**
 * Live PDF of the template with the example values typed in the grid. Rendered in the
 * browser by the same Typst engine the server uses, so this is exactly what will print.
 */
export function PreviewPane({ doc, computed }: { doc: TemplateDoc; computed: ComputedSection }) {
  const [pdf, setPdf] = useState<Uint8Array | null>(null);
  const [status, setStatus] = useState(
    'Starting the PDF engine. This takes a few seconds the first time.',
  );
  const [error, setError] = useState<string | null>(null);
  const latest = useRef(0);

  useEffect(() => {
    const ticket = ++latest.current;
    const timer = setTimeout(async () => {
      const now = new Date();
      const data: ReportData = {
        meta: {
          title: `Preview · ${doc.name}`,
          reportId: 'PREVIEW',
          patientName: doc.name,
          date: {
            year: now.getUTCFullYear(),
            month: now.getUTCMonth() + 1,
            day: now.getUTCDate(),
            hour: now.getUTCHours(),
            minute: now.getUTCMinutes(),
          },
        },
        lab: {
          name: 'YOUR LAB NAME',
          address: 'Your address appears here once Report Settings are filled in',
          phones: '+91 00000 00000',
          accent: '#2b2157',
          registrations: [],
        },
        // What the template itself says, instead of a made-up patient.
        patient: {
          left: [
            ['Test', doc.name],
            ...(doc.department ? [['Department', doc.department] as [string, string]] : []),
            ...(doc.sampleType ? [['Sample', doc.sampleType] as [string, string]] : []),
            ...(doc.instrument ? [['Instrument', doc.instrument] as [string, string]] : []),
          ],
          right: [['Status', 'Template preview']],
        },
        sections: [toPrintSection(doc, computed)],
        signatories: [],
        options: {
          letterhead: false,
          topMarginMm: 50,
          bottomMarginMm: 18,
          signaturesOn: 'last-page',
          showEndOfReport: true,
        },
      };
      const res = await renderPdf(data);
      if (ticket !== latest.current) return;
      if (res.error || !res.pdf) {
        setError(res.error ?? 'The PDF could not be made');
        return;
      }
      setError(null);
      setStatus(`Rendered on this device in ${res.ms.toFixed(0)} ms`);
      setPdf(res.pdf);
    }, 250);
    return () => clearTimeout(timer);
  }, [doc, computed]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-canvas">
      <div className="flex h-8 items-center px-3 text-[12px] text-slate" role="status">
        {error ? <span className="text-flag">{error}</span> : status}
      </div>
      {pdf ? (
        <Suspense
          fallback={
            <div className="grid flex-1 place-items-center text-slate">Loading preview…</div>
          }
        >
          <PdfPages pdf={pdf} />
        </Suspense>
      ) : (
        <div className="grid flex-1 place-items-center text-slate">Loading preview…</div>
      )}
    </div>
  );
}
